import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  Card,
  CardContent,
  CardActionArea,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Visibility from '@mui/icons-material/Visibility';
import evaluationsApi, { EvaluationListItem } from '../services/evaluationService';
import StatusPill from '../components/StatusPill';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const STATUS_FILTERS: { value: '' | 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED'; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

const STATUS_VALUE_MAP: Record<
  string,
  { label: string; color?: 'success' | 'warning' | 'error' | 'default' }
> = {
  DRAFT: { label: 'Draft', color: 'default' },
  SUBMITTED: { label: 'Submitted', color: 'warning' },
  APPROVED: { label: 'Approved', color: 'success' },
  REJECTED: { label: 'Rejected', color: 'error' },
};

function formatCurrency(amount: number | null | undefined): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount ?? 0);
}

function formatListDate(ev: EvaluationListItem): string {
  const iso = ev.submitted_at ?? ev.approved_at ?? ev.created_at;
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
}

function evaluationHref(ev: EvaluationListItem, userId: number | null | undefined): string {
  const st = (ev.status || '').toUpperCase();
  if (st === 'SUBMITTED' && userId != null && ev.approver_id === userId) {
    return `/evaluations/${ev.id}/approve`;
  }
  return `/evaluations/${ev.id}`;
}

const EvaluationsListPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]['value']>('');
  const [rows, setRows] = useState<EvaluationListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const param = statusFilter || undefined;
    evaluationsApi
      .list(param)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const goToEvaluation = (ev: EvaluationListItem) => {
    navigate(evaluationHref(ev, user?.id ?? null));
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h4" component="h1">
          Evaluations
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/evaluations/new')}
          sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
        >
          New Evaluation
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mb: 2,
          overflowX: 'auto',
          flexWrap: { xs: 'nowrap', sm: 'wrap' },
          pb: 0.5,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {STATUS_FILTERS.map((f) => {
          const selected = statusFilter === f.value;
          return (
            <Chip
              key={f.value || 'all'}
              label={f.label}
              onClick={() => setStatusFilter(f.value)}
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              size="small"
              sx={{ flexShrink: 0 }}
            />
          );
        })}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {rows.length === 0 ? (
            <Typography color="text.secondary">No evaluations found.</Typography>
          ) : (
            rows.map((ev) => {
              const statusKey = (ev.status || '').toUpperCase();
              return (
                <Card key={ev.id} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardActionArea onClick={() => goToEvaluation(ev)}>
                    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight={600}>
                          {ev.evaluation_number}
                        </Typography>
                        <StatusPill value={statusKey} valueMap={STATUS_VALUE_MAP} />
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {ev.company_name}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {ev.sales_person_name}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mt: 1,
                          flexWrap: 'wrap',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(ev.total_ext_price)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ev.item_count} {ev.item_count === 1 ? 'item' : 'items'}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        {formatListDate(ev)}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })
          )}
        </Box>
      ) : (
        <Paper sx={{ p: { xs: 1, sm: 2 }, overflow: 'hidden' }}>
          <TableContainer sx={{ maxWidth: '100%' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Sales Person</TableCell>
                  <TableCell>Approver</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Items</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10}>
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        No evaluations found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((ev) => {
                    const statusKey = (ev.status || '').toUpperCase();
                    return (
                      <TableRow
                        key={ev.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => goToEvaluation(ev)}
                      >
                        <TableCell>{ev.evaluation_number}</TableCell>
                        <TableCell>{ev.company_name}</TableCell>
                        <TableCell>{ev.company_contact_name ?? '—'}</TableCell>
                        <TableCell>{ev.sales_person_name}</TableCell>
                        <TableCell>{ev.approver_name}</TableCell>
                        <TableCell>
                          <StatusPill value={statusKey} valueMap={STATUS_VALUE_MAP} />
                        </TableCell>
                        <TableCell align="right">{ev.item_count}</TableCell>
                        <TableCell align="right">{formatCurrency(ev.total_ext_price)}</TableCell>
                        <TableCell>{formatListDate(ev)}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            aria-label="view"
                            onClick={(e) => {
                              e.stopPropagation();
                              goToEvaluation(ev);
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default EvaluationsListPage;
